/**
 * Authentication Page
 *
 * Provides:
 * - Sign in form
 * - Sign up form
 * - Magic link (passwordless) option
 * - Toggle between modes
 * - Fidelity-style layout with disabled navigation
 */

import { useState, type FormEvent } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { AuthLayout } from '@/components/Layout/AuthLayout';
import './Auth.css';

export function Auth(): JSX.Element {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);

  const { signIn, signUp, signInWithMagicLink } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const result = mode === 'signin'
        ? await signIn(email, password)
        : await signUp(email, password, displayName);

      if (result.success) {
        navigate('/gym');
      } else {
        setError(result.error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (): Promise<void> => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setError(null);
    setSuccess(null);
    setMagicLinkLoading(true);

    try {
      const result = await signInWithMagicLink(email);

      if (result.success) {
        setSuccess('Check your email for a sign-in link!');
      } else {
        setError(result.error);
      }
    } finally {
      setMagicLinkLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="auth">
        <div className="auth__header">
          <div className="auth__logo">
            <div className="auth__logo-circle">
              <span className="auth__logo-icon">M</span>
            </div>
          </div>
          <p className="auth__welcome-text">Welcome</p>
        </div>

        <p className="auth__description">
          Your personal fitness and productivity companion.
        </p>

        <div className="auth__container">
          <h2 className="auth__title">
            {mode === 'signin' ? 'Sign In' : 'Create Account'}
          </h2>

            <form className="auth__form" onSubmit={handleSubmit}>
              {mode === 'signup' && (
                <div className="auth__field">
                  <label htmlFor="displayName" className="auth__label">
                    Display Name
                  </label>
                  <input
                    id="displayName"
                    type="text"
                    className="auth__input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="John Doe"
                  />
                </div>
              )}

              <div className="auth__field">
                <label htmlFor="email" className="auth__label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="auth__input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div className="auth__field">
                <label htmlFor="password" className="auth__label">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  className="auth__input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>

              {error && (
                <div className="auth__error">
                  {error}
                </div>
              )}

              {success && (
                <div className="auth__success">
                  {success}
                </div>
              )}

              <button
                type="submit"
                className="auth__submit"
                disabled={loading || magicLinkLoading}
              >
                {loading ? 'Please wait...' : mode === 'signin' ? 'Sign In' : 'Sign Up'}
              </button>
            </form>

            {mode === 'signin' && (
              <div className="auth__divider">
                <span className="auth__divider-text">or continue with</span>
              </div>
            )}

            {mode === 'signin' && (
              <button
                type="button"
                className="auth__magic-link"
                onClick={handleMagicLink}
                disabled={loading || magicLinkLoading}
              >
                {magicLinkLoading ? 'Sending...' : 'Sign in with Email Link'}
              </button>
            )}

            <div className="auth__toggle">
              {mode === 'signin' ? (
                <>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="auth__toggle-button"
                    onClick={() => {
                      setMode('signup');
                      setError(null);
                      setSuccess(null);
                    }}
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="auth__toggle-button"
                    onClick={() => {
                      setMode('signin');
                      setError(null);
                      setSuccess(null);
                    }}
                  >
                    Sign In
                  </button>
                </>
              )}
            </div>

            <div className="auth__about-link">
              <Link to="/about" className="auth__about-button">
                Learn more about me
              </Link>
            </div>
          </div>
        </div>
      </AuthLayout>
    );
}
